/**
 * Document kinds for the Library (`documents` table, plan §8.4:
 * docs/claudedocs/presales-library-and-sequences-plan.md). Single source of
 * truth — adding a kind is a change here plus one migration updating the
 * `kind` CHECK (packages/db/init/150-documents.sql), never a rename.
 *
 * Every kind belongs to exactly one group (asserted by a spec, not just by
 * convention) — see `apps/api/src/modules/library/__tests__/document-kinds.spec.ts`.
 *
 * `demo_sample` and `recorded_export` were added 2026-09-18 for the
 * upload/download-in-demos feature
 * (docs/claudedocs/requirements/file-upload-download-in-demos-2026-09-18.md
 * §2.2, §2.5): `demo_sample` is an owner-supplied file filling a flow step's
 * file slot; `recorded_export` is a file the recorder captured coming out of
 * a download step. Both land in Other — a seventh group for two kinds isn't
 * worth it yet; see the requirement doc for when that changes.
 *
 * Pure data — no Node globals at module eval, safe for the web bundle.
 */
export const DOCUMENT_KIND_GROUPS = {
    legal: ['nda', 'msa', 'sales_agreement', 'order_form', 'dpa', 'sla', 'terms'],
    commercial: ['brochure', 'one_pager', 'deck', 'pricing', 'proposal', 'quote', 'roi_model'],
    proof: ['case_study', 'testimonial', 'reference_list', 'benchmark', 'analyst_report', 'award'],
    technical: [
        'architecture',
        'security_overview',
        'compliance_certificate',
        'integration_guide',
        'api_reference',
        'data_sheet',
    ],
    onboarding: ['implementation_plan', 'onboarding_guide', 'training_material', 'faq', 'support_policy'],
    other: ['reference', 'other', 'demo_sample', 'recorded_export'],
};
export const DOCUMENT_KINDS = Object.values(DOCUMENT_KIND_GROUPS).flat();
export function isDocumentKind(value) {
    return DOCUMENT_KINDS.includes(value);
}
/** Reverse lookup, kind -> its group. Computed once at module load. */
export const DOCUMENT_KIND_GROUP_BY_KIND = Object.fromEntries(Object.entries(DOCUMENT_KIND_GROUPS).flatMap(([group, kinds]) => kinds.map((kind) => [kind, group])));
/** Audience gate — mirrors the `documents.audience` CHECK. `prospect` is the Library's own gate for offerable/sendable documents (plan §3.2, §3.4). */
export const DOCUMENT_AUDIENCES = ['internal', 'prospect'];
/** `document_sends.channel` CHECK. */
export const DOCUMENT_SEND_CHANNELS = ['email', 'whatsapp', 'link'];
/**
 * Document PURPOSE — delivery intent, owner-set at upload (migration 177).
 * Distinct from `kind` (type taxonomy) and `audience` (send gate): purpose is
 * what the agent should DO with the doc, and drives the generated brief
 * (speaker/delivery notes, reference summary, objection prep). Mirrors the
 * `documents.purpose` CHECK — one source of truth.
 */
export const DOCUMENT_PURPOSES = ['presentation', 'reference', 'pricing_proposal', 'legal_contract'];
/** Sub-type of a `presentation` purpose. Mirrors the `documents.presentation_subtype` CHECK. */
export const PRESENTATION_SUBTYPES = ['sales_pitch', 'investor_pitch', 'product_features', 'company_overview'];
/** Voice the agent uses when presenting. Mirrors the `documents.tone` CHECK. */
export const DOCUMENT_TONES = ['neutral', 'authoritative', 'conversational', 'enthusiastic'];
export function isDocumentPurpose(value) {
    return DOCUMENT_PURPOSES.includes(value);
}
//# sourceMappingURL=document-kinds.js.map